import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  MenuItem,
  TextareaAutosize,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  useGetSprintListQuery,
  useLazyGetSprintByIdQuery,
} from "@/features/bughurd/api";
import {
  StyledChip,
  StyledSelect,
  StyledTextField,
} from "../../bugherdComponents/styled";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

interface SprintStartModalProps {
  open: boolean;
  onClose: () => void;
  onStart: (data: SprintStartData) => void;
  sprint: {
    _id: string;
    name: string;
    status: string;
  };
  taskCount: number;
  isLoading?: boolean;
  isEditMode?: boolean;
}

interface SprintStartData {
  sprintId: string;
  name: string;
  startDate: string;
  endDate: string;
  duration: string;
  goal: string;
}

const SprintStartModal: React.FC<SprintStartModalProps> = ({
  open,
  onClose,
  onStart,
  sprint,
  taskCount,
  isLoading = false,
  isEditMode = false,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const { id } = useParams<{ id: string }>();
  const { data: sprintListData } = useGetSprintListQuery({
    projectId: id || "",
    page: 1,
    limit: 100,
    statuses: ["ACTIVE"],
  });

  // Use lazy query to have more control over when it's called
  const [
    getSprintDetails,
    {
      data: sprintDetailsData,
      isLoading: isLoadingDetails,
      error: sprintDetailsError,
    },
  ] = useLazyGetSprintByIdQuery();
  const [formData, setFormData] = useState<SprintStartData>({
    sprintId: sprint._id,
    name: sprint.name,
    startDate: "",
    endDate: "",
    duration: "1 week",
    goal: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [hasTriedToLoadDetails, setHasTriedToLoadDetails] = useState(false);

  // Duration options
  const durationOptions = [
    { value: "1 week", label: t("1 week") },
    { value: "2 weeks", label: t("2 weeks") },
    { value: "3 weeks", label: t("3 weeks") },
    { value: "custom", label: t("Custom") },
  ];

  // Calculate duration based on start and end dates
  const calculateDurationFromDates = (
    startDate: string,
    endDate: string
  ): string => {
    if (!startDate || !endDate) return "custom";

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.ceil(diffDays / 7);

    switch (diffWeeks) {
      case 1:
        return "1 week";
      case 2:
        return "2 weeks";
      case 3:
        return "3 weeks";
      default:
        return "custom";
    }
  };

  // Calculate end date based on start date and duration
  const calculateEndDate = (startDate: string, duration: string): string => {
    if (!startDate || duration === "custom") return "";

    const start = new Date(startDate);
    let weeks = 1;

    switch (duration) {
      case "1 week":
        weeks = 1;
        break;
      case "2 weeks":
        weeks = 2;
        break;
      case "3 weeks":
        weeks = 3;
        break;
      default:
        weeks = 1;
    }

    const end = new Date(start);
    end.setDate(start.getDate() + weeks * 7);

    return end.toISOString().split("T")[0];
  };

  // Fetch sprint details when modal opens in edit mode
  useEffect(() => {
    if (open && isEditMode && !hasTriedToLoadDetails) {
      setHasTriedToLoadDetails(true);
      getSprintDetails({ sprintId: sprint._id, timestamp: Date.now() }).catch(
        (error) => {
          console.error("Error fetching sprint details:", error);
        }
      );
    }
  }, [open, isEditMode, sprint._id, hasTriedToLoadDetails, getSprintDetails]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setIsFormInitialized(false);
      setHasTriedToLoadDetails(false);
      setErrors({});
      setFormData({
        sprintId: sprint._id,
        name: sprint.name,
        startDate: "",
        endDate: "",
        duration: "1 week",
        goal: "",
      });
    }
  }, [open, sprint]);

  // Initialize form data when modal opens or sprint details load
  useEffect(() => {
    if (open && !isFormInitialized) {
      if (isEditMode && sprintDetailsData?.data?.decryptedData) {
        // Edit mode with loaded details
        const sprintDetails = sprintDetailsData.data.decryptedData;

        // Format dates properly
        const startDate = sprintDetails.startDate
          ? new Date(sprintDetails.startDate).toISOString().split("T")[0]
          : "";
        const endDate = sprintDetails.endDate
          ? new Date(sprintDetails.endDate).toISOString().split("T")[0]
          : "";

        const duration = calculateDurationFromDates(startDate, endDate);

        setFormData({
          sprintId: sprint._id,
          name: sprintDetails.name || sprint.name,
          startDate: startDate,
          endDate: endDate,
          duration: duration,
          goal: sprintDetails.description || "",
        });
        setIsFormInitialized(true);
      } else if (isEditMode && (isLoadingDetails || !hasTriedToLoadDetails)) {
        // Still loading details or haven't tried to load yet
        return;
      } else if (!isEditMode) {
        // Create/Start mode
        const today = new Date().toISOString().split("T")[0];
        const calculatedEndDate = calculateEndDate(today, "1 week");

        setFormData({
          sprintId: sprint._id,
          name: sprint.name,
          startDate: today,
          endDate: calculatedEndDate,
          duration: "1 week",
          goal: "",
        });
        setIsFormInitialized(true);
      }
      setErrors({});
    }
  }, [
    open,
    sprint,
    isEditMode,
    sprintDetailsData,
    isLoadingDetails,
    isFormInitialized,
    hasTriedToLoadDetails,
  ]);

  // Handle API error
  useEffect(() => {
    if (sprintDetailsError && open && isEditMode) {
      console.error("Error loading sprint details:", sprintDetailsError);
      // Initialize with basic data if API fails
      const today = new Date().toISOString().split("T")[0];
      setFormData({
        sprintId: sprint._id,
        name: sprint.name,
        startDate: today,
        endDate: calculateEndDate(today, "1 week"),
        duration: "1 week",
        goal: "",
      });
      setIsFormInitialized(true);
    }
  }, [sprintDetailsError, open, isEditMode, sprint]);

  // Validation
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = t("Sprint name is required");
    }

    if (!formData.startDate) {
      newErrors.startDate = t("Start date is required");
    }

    if (!formData.endDate) {
      newErrors.endDate = t("End date is required");
    }

    if (formData.startDate && formData.endDate) {
      if (new Date(formData.endDate) <= new Date(formData.startDate)) {
        newErrors.endDate = t("End date must be after start date");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleStart = () => {
    if (validateForm()) {
      if (sprintListData?.data?.decryptedData?.items?.length === 0) {
        onStart(formData);
      } else {
        toast.error(t("Only one Active sprint is allowed at a time."));
      }
    }
  };

  // Handle input changes
  const handleInputChange = (field: keyof SprintStartData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Handle duration change - FIXED
  const handleDurationChange = (value: string) => {
    setFormData((prev) => {
      let newEndDate = prev.endDate;

      // If not custom and we have a start date, calculate new end date
      if (value !== "custom" && prev.startDate) {
        newEndDate = calculateEndDate(prev.startDate, value);
      }

      return {
        ...prev,
        duration: value,
        endDate: newEndDate,
      };
    });

    // Clear end date error if exists
    if (errors.endDate) {
      setErrors((prev) => ({
        ...prev,
        endDate: "",
      }));
    }
  };

  // Handle start date change - FIXED
  const handleStartDateChange = (value: string) => {
    setFormData((prev) => {
      let newEndDate = prev.endDate;

      // If duration is not custom, recalculate end date
      if (prev.duration !== "custom" && value) {
        newEndDate = calculateEndDate(value, prev.duration);
      }

      return {
        ...prev,
        startDate: value,
        endDate: newEndDate,
      };
    });

    // Clear errors
    if (errors.startDate) {
      setErrors((prev) => ({
        ...prev,
        startDate: "",
        endDate: "", // Clear end date error too
      }));
    }
  };

  // Handle end date change - FIXED
  const handleEndDateChange = (value: string) => {
    setFormData((prev) => {
      // When manually changing end date, switch to custom duration
      const newDuration =
        prev.duration === "custom"
          ? "custom"
          : calculateDurationFromDates(prev.startDate, value);

      return {
        ...prev,
        endDate: value,
        duration: newDuration === prev.duration ? prev.duration : "custom",
      };
    });

    // Clear error
    if (errors.endDate) {
      setErrors((prev) => ({
        ...prev,
        endDate: "",
      }));
    }
  };

  // Show loading state while fetching sprint details in edit mode
  if (
    isEditMode &&
    (isLoadingDetails ||
      (!sprintDetailsData && !sprintDetailsError && hasTriedToLoadDetails))
  ) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogContent
          sx={{ display: "flex", justifyContent: "center", py: 4 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {t("Loading sprint details...")}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Show error state if failed to load sprint details
  if (isEditMode && sprintDetailsError) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {t("Error")}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="error" sx={{ mb: 3 }}>
            {t("Failed to load sprint details. Please try again.")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            size="large"
            sx={{
              minWidth: 100,
              textTransform: "none",
            }}
          >
            {t("Close")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" fontWeight={600}>
          {isEditMode ? t("Edit Sprint") : t("Start Sprint")}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("{{count}} work item will be included in this sprint.", {
            count: taskCount,
          })}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("Required fields are marked with an asterisk")}{" "}
          <span style={{ color: "#f44336" }}>*</span>
        </Typography>

        {/* Sprint Name */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            {t("Sprint name")} <span style={{ color: "#f44336" }}>*</span>
          </Typography>
          <StyledTextField
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            placeholder={t("Enter sprint name")}
            fullWidth
            size="small"
          />
        </Box>

        {/* Duration */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            {t("Duration")} <span style={{ color: "#f44336" }}>*</span>
          </Typography>
          <StyledSelect
            value={formData.duration}
            onChange={(e) => handleDurationChange(e.target.value)}
            fullWidth
            size="small"
          >
            {durationOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </StyledSelect>
        </Box>

        {/* Start Date */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            {t("Start date")} <span style={{ color: "#f44336" }}>*</span>
          </Typography>
          <StyledTextField
            type="date"
            value={formData.startDate}
            onChange={(e) => handleStartDateChange(e.target.value)}
            error={!!errors.startDate}
            helperText={errors.startDate}
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
            size="small"
          />
        </Box>

        {/* End Date */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            {t("End date")} <span style={{ color: "#f44336" }}>*</span>
          </Typography>
          <StyledTextField
            type="date"
            value={formData.endDate}
            onChange={(e) => handleEndDateChange(e.target.value)}
            error={!!errors.endDate}
            helperText={errors.endDate}
            disabled={formData.duration !== "custom"}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: formData.startDate || undefined,
            }}
            fullWidth
            size="small"
            sx={{
              "& .Mui-disabled": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
          {formData.duration !== "custom" && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              {t("End date is automatically calculated based on duration")}
            </Typography>
          )}
        </Box>

        {/* Sprint Goal */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            {t("Sprint goal")}
          </Typography>
          <TextareaAutosize
            minRows={4}
            value={formData.goal}
            onChange={(e) => handleInputChange("goal", e.target.value)}
            placeholder={t("Enter sprint goal (optional)")}
            style={{
              width: "100%",
              padding: "12px",
              border: "1px solid #d0d7de",
              borderRadius: "6px",
              fontSize: "14px",
              fontFamily: "inherit",
              resize: "vertical",
              outline: "none",
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="large"
          disabled={isLoading}
          sx={{
            minWidth: 100,
            textTransform: "none",
          }}
        >
          {t("Cancel")}
        </Button>
        <Button
          onClick={handleStart}
          variant="contained"
          size="large"
          disabled={isLoading}
          sx={{
            minWidth: 100,
          }}
        >
          {isLoading
            ? isEditMode
              ? t("Updating...")
              : t("Starting...")
            : isEditMode
            ? t("Update")
            : t("Start")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SprintStartModal;
